import { Outlet } from "react-router-dom";
import * as React from 'react';
import { CssVarsProvider } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box, { BoxProps } from '@mui/joy/Box';
import Navigation from '../components/email/Navigation.tsx';
import Header from '../components/email/Header.tsx';
import Sheet from '@mui/joy/Sheet';

export const LayoutComponents = {
  Root: (props: BoxProps) => {
    return (
      <Box
        {...props}
        sx={[
          {
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(64px, 200px) minmax(450px, 1fr)',
              md: 'minmax(160px, 300px) minmax(500px, 1fr)',
            },
            gridTemplateRows: '64px 1fr',
            minHeight: '100vh',
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      />
    );
  },
  Header: (props: BoxProps) => {
    return (
      <Box
        component="header"
        className="Header"
        {...props}
        sx={[
          {
            p: 2,
            gap: 2,
            bgcolor: 'background.surface',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            gridColumn: '1 / -1',
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            zIndex: 1100,
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      />
    );
  },
  SideNav: (props: BoxProps) => {
    return (
      <Box
        component="nav"
        className="Navigation"
        {...props}
        sx={[
          {
            p: 2,
            bgcolor: 'background.surface',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: {
              xs: 'none',
              sm: 'initial',
            },
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      />
    );
  },
  SidePane: (props: BoxProps) => {
    return (
      <Box
        className="Inbox"
        {...props}
        sx={[
          {
            bgcolor: 'background.surface',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: {
              xs: 'initial',
              md: 'initial',
            },
          },
          ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
        ]}
      />
    );
  },
  Main: (props: BoxProps) => {
    return (
      <Box
        component="main"
        className="Main"
        {...props}
        sx={[{ p: 2 }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
      />
    );
  },
  SideDrawer: (
    props: BoxProps & { onClose: React.MouseEventHandler<HTMLDivElement> },
  ) => {
    const { onClose, ...other } = props;
    return (
      <Box
        {...other}
        sx={[
          { position: 'fixed', zIndex: 1200, width: '100%', height: '100%' },
          ...(Array.isArray(other.sx) ? other.sx : [other.sx]),
        ]}
      >
        <Box
          role="button"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            inset: 0,
            bgcolor: `rgba(${theme.vars.palette.neutral.darkChannel} / 0.8)`,
          })}
        />
        <Sheet
          sx={{
            minWidth: 256,
            width: 'max-content',
            height: '100%',
            p: 2,
            boxShadow: 'lg',
            bgcolor: 'background.surface',
          }}
        >
          {props.children}
        </Sheet>
      </Box>
    );
  }
}

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  return (
    <CssVarsProvider disableTransitionOnChange>
      <CssBaseline />
      {drawerOpen && (
        <LayoutComponents.SideDrawer onClose={() => setDrawerOpen(false)}>
          <Navigation />
        </LayoutComponents.SideDrawer>
      )}
      <LayoutComponents.Root
        sx={[
          drawerOpen && {
            height: '100vh',
            overflow: 'hidden',
          },
        ]}
      >
        <LayoutComponents.Header>
          <Header />
        </LayoutComponents.Header>
        <LayoutComponents.SideNav>
          <Navigation />
        </LayoutComponents.SideNav>
        <LayoutComponents.SidePane>
          <Outlet />
        </LayoutComponents.SidePane>
      </LayoutComponents.Root>
    </CssVarsProvider>
  );
}